import * as angular from 'angular';
import { TogglePanelCallback } from 'angular/custom/components/toggle-panel.component';
import { Growl } from 'angular/custom/services/growl.service';
import { CreditFrameworkResource } from 'app/_resources/credit-framework.resource';
import { TransmissionSystemOperatorResource } from 'app/_resources/transmission-system-operator.resource';
import { AuthService } from 'app/_services/auth.service';
import { ConfirmModal } from 'app/_services/confirm-modal.factory';
import { ErrorHandler } from 'app/_services/error-handler.factory';

class OperatorSettingsCreditLimitsCtrl implements ng.IController {
    private creditLimitCheck: boolean;
    private loading: boolean;
    public loadingCreditFrameworks: boolean = true;
    private loadingErrMsg: string;
    private loadingCreditFrameworksErrMsg: string;
    private settings: TsoCreditLimitSettingsDto;
    private loadingEnableForAuctionsAndFcfs: boolean;
    private loadingEnableForSecondaryMarket: boolean;
    private loadingShowCreditLimitsToShippers: boolean;
    private enabledSettings: PossibleSettings[];
    public isNewFramework: boolean = false;
    public uniqueFrameworkNames: any = [];

    public creditFrameworks: CreditFrameworkDto[];
    private loggedInUser: LoginResult;

    constructor(
        private confirmModal: ConfirmModal,
        private TransmissionSystemOperator: TransmissionSystemOperatorResource,
        private creditFrameworkResource: CreditFrameworkResource,
        private $timeout: ng.ITimeoutService,
        private errorHandler: ErrorHandler,
        private authService: AuthService,
        private growl: Growl
    ) {}

    $onInit(): void {
        this.loading = true;
        this.authService.userLoginPromise.promise.then(
            (loggedInUser: LoginResult) => {
                this.loggedInUser = loggedInUser;

                this.TransmissionSystemOperator.getCreditLimitSettings(
                    { id: this.loggedInUser.referenceId },
                    (res: TsoCreditLimitSettingsDto) => {
                        this.settings = res;
                        this.loadingErrMsg = null;

                        this.creditLimitCheck =
                            this.settings.enableForAuctionsAndFcfs ||
                            this.settings.enableForSecondaryMarket;
                    },
                    (err: ng.IHttpPromiseCallbackArg<RestError>) => {
                        this.loadingErrMsg =
                            err && err.data && err.data.message
                                ? err.data.message
                                : 'Sorry, an unknown error occurred. Please, try again later.';
                    }
                ).$promise.finally(() => (this.loading = false));

                this.getCreditFrameworks();
            }
        );
    }

    private getCreditFrameworks() {
        this.loadingCreditFrameworks = true;
        this.creditFrameworkResource
            .getCreditFrameworksForLoggedInTso(
                (data: CreditFrameworkDto[]) => {
                    this.creditFrameworks = data;
                    this.loadingCreditFrameworks = false;
                    this.loadingCreditFrameworksErrMsg = null;

                    if (this.creditFrameworks.length > 0) {
                        this.creditFrameworks.forEach((creditFramework) =>
                            this.uniqueFrameworkNames.push(
                                creditFramework.refLabel
                            )
                        );
                    }
                },
                (dataError: ng.IHttpPromiseCallbackArg<RestError>) => {
                    this.loadingCreditFrameworksErrMsg =
                        dataError.data && dataError.data.message
                            ? dataError.data.message
                            : 'Sorry, an unknown error occurred. Please, try again later.';
                }
            )
            .$promise.finally(() => (this.loadingCreditFrameworks = false));
    }

    public toggleCreditLimitCheck(cb: TogglePanelCallback): void {
        if (
            !this.settings.enableForAuctionsAndFcfs &&
            !this.settings.enableForSecondaryMarket
        ) {
            cb(true);
            return;
        }

        this.TransmissionSystemOperator.deactivateCreditLimitSettings(
            { id: this.loggedInUser.referenceId },
            () => {
                try {
                    this.settings.enableForSecondaryMarket = false;
                    this.settings.enableForAuctionsAndFcfs = false;
                    this.settings.showCreditLimitsToShippers = false;

                    if (
                        this.enabledSettings.indexOf('CREDIT_LIMIT_FCFS') !== -1
                    ) {
                        this.enabledSettings.splice(
                            this.enabledSettings.indexOf('CREDIT_LIMIT_FCFS'),
                            1
                        );
                    }
                    if (
                        this.enabledSettings.indexOf(
                            'CREDIT_LIMIT_SECONDARY'
                        ) !== -1
                    ) {
                        this.enabledSettings.splice(
                            this.enabledSettings.indexOf(
                                'CREDIT_LIMIT_SECONDARY'
                            ),
                            1
                        );
                    }

                    this.growl.show('Successfully saved.', 'success');
                    cb(true);
                } catch (e) {
                    cb(false);
                    throw e;
                }
            },
            (err) => {
                cb(false);
                this.errorHandler(err);
            }
        );
    }

    public toggleSetting(
        onConfirmation: () => ng.IPromise<any>,
        resetSetting: () => void,
        onFinally: () => void
    ): void {
        this.askForConfirmation()
            .result.then(() =>
                onConfirmation()
                    .then(() =>
                        this.growl.show('Successfully saved.', 'success', {
                            delay: 2000
                        })
                    )
                    .catch((err: ng.IHttpPromiseCallbackArg<RestError>) => {
                        resetSetting();
                        this.errorHandler(err);
                    })
                    .finally(onFinally)
            )
            .catch(resetSetting.bind(this));
    }

    public toggleEnableForAuctionsAndFcfs(): void {
        this.toggleSetting(
            () => {
                this.loadingEnableForAuctionsAndFcfs = true;
                this.changeEnabledSetting(
                    this.settings.enableForAuctionsAndFcfs,
                    'CREDIT_LIMIT_FCFS'
                );

                return this.TransmissionSystemOperator.enableCreditLimitsForAuctionsAndFcfs(
                    { id: this.loggedInUser.referenceId },
                    this.settings.enableForAuctionsAndFcfs
                ).$promise;
            },
            () =>
                (this.settings.enableForAuctionsAndFcfs =
                    !this.settings.enableForAuctionsAndFcfs),
            () => (this.loadingEnableForAuctionsAndFcfs = false)
        );
    }

    public toggleEnableForSecondaryMarket(): void {
        this.toggleSetting(
            () => {
                this.loadingEnableForSecondaryMarket = true;
                this.changeEnabledSetting(
                    this.settings.enableForSecondaryMarket,
                    'CREDIT_LIMIT_SECONDARY'
                );

                return this.TransmissionSystemOperator.enableCreditLimitsForSecondaryMarket(
                    { id: this.loggedInUser.referenceId },
                    this.settings.enableForSecondaryMarket
                ).$promise;
            },
            () =>
                (this.settings.enableForSecondaryMarket =
                    !this.settings.enableForSecondaryMarket),
            () => (this.loadingEnableForSecondaryMarket = false)
        );
    }

    public toggleShowCreditLimitsToShippers(): void {
        this.toggleSetting(
            () => {
                this.loadingShowCreditLimitsToShippers = true;
                return this.TransmissionSystemOperator.showCreditLimitsToShippers(
                    { id: this.loggedInUser.referenceId },
                    this.settings.showCreditLimitsToShippers
                ).$promise;
            },
            () =>
                (this.settings.showCreditLimitsToShippers =
                    !this.settings.showCreditLimitsToShippers),
            () => (this.loadingShowCreditLimitsToShippers = false)
        );
    }

    public askForConfirmation(
        text: string = 'Do you really want to change this setting?'
    ): angular.ui.bootstrap.IModalInstanceService {
        return this.confirmModal(text);
    }

    private changeEnabledSetting(
        enableSetting: boolean,
        setting: PossibleSettings
    ) {
        if (enableSetting) {
            this.enabledSettings.push(setting);
        } else {
            this.enabledSettings.splice(
                this.enabledSettings.indexOf(setting),
                1
            );
        }
    }

    public addCreatedFramework(newFramework) {
        this.getCreditFrameworks();
    }
}

angular.module('app').component('pkpOperatorSettingsCreditLimits', {
    controller: OperatorSettingsCreditLimitsCtrl,
    template:
        require('app/operator-settings/operator-settings-credit-limits.component.html'),
    bindings: {
        enabledSettings: '='
    }
});
